import dayjs from "dayjs";
import type { Lead } from "./types";
import { createLeadGenId, createTouchId, sortTouchesByDate, type Touch } from "./types";
import {
    DEFAULT_OUTREACH_TEMPLATES,
    getDefaultOutreachTemplateId,
    getOutreachTemplate,
    type OutreachTemplate,
    type OutreachTemplateTiming,
} from "./templates";

const ISO_DATE = "YYYY-MM-DD";

function scheduledTouch(
    kind: Touch["kind"],
    date: string | null,
    status: Touch["status"] = "todo",
    message = ""
): Touch {
    return {
        touch_id: createTouchId(),
        kind,
        date,
        status,
        message,
    };
}

function withConnectOnAddDay(timing: OutreachTemplateTiming[]): OutreachTemplateTiming[] {
    if (timing.some((step) => step.kind === "connect")) return timing;
    return [{ kind: "connect", offset_days: 0 }, ...timing];
}

/** Default cadence for a new lead: connect today, then empty scheduled touches. */
export function defaultLeadTiming(
    templates: OutreachTemplate[] = DEFAULT_OUTREACH_TEMPLATES
): OutreachTemplateTiming[] {
    const template = getOutreachTemplate(templates, getDefaultOutreachTemplateId(templates));
    const timing = template?.timing?.length ? template.timing : (DEFAULT_OUTREACH_TEMPLATES[0]?.timing ?? []);
    return withConnectOnAddDay(timing);
}

export function scheduleFromTiming(
    connectDate: string | Date | dayjs.Dayjs,
    timing: OutreachTemplateTiming[],
    messages: string[] = []
): Pick<Lead, "touches"> {
    const connect = dayjs(connectDate);

    return {
        touches: sortTouchesByDate(
            timing.map((step, index) => {
                const date = connect.add(step.offset_days, "day").format(ISO_DATE);
                const isConnect = step.kind === "connect";
                return scheduledTouch(
                    step.kind,
                    date,
                    isConnect ? "sent" : "draft",
                    isConnect ? "" : (messages[index] ?? "")
                );
            })
        ),
    };
}

export function createLeadFromForm(args: {
    name: string;
    role?: string;
    company: string;
    team: string;
    linkedin_url: string;
    email?: string;
    summary?: string;
    status?: Lead["status"];
    template?: OutreachTemplate;
    timing?: OutreachTemplateTiming[];
    connectDate?: string | Date | dayjs.Dayjs;
    messages?: string[];
}): Lead {
    const connectDate = args.connectDate ?? dayjs();
    const timing = args.timing ?? args.template?.timing ?? defaultLeadTiming();

    return {
        lead_id: createLeadGenId(),
        name: args.name.trim() || "New lead",
        role: (args.role ?? "").trim(),
        company: args.company.trim(),
        team: args.team.trim(),
        linkedin_url: args.linkedin_url.trim(),
        email: (args.email ?? "").trim(),
        email_status: (args.email ?? "").trim() ? "found" : "not_started",
        summary: (args.summary ?? "").trim(),
        status: args.status ?? "in_progress",
        ...scheduleFromTiming(
            connectDate,
            withConnectOnAddDay(timing.length ? timing : defaultLeadTiming()),
            args.messages ?? []
        ),
    };
}

export function formatLeadDate(value: string | null): string {
    if (!value) return "-";
    return dayjs(value).format("D MMM YYYY");
}

export function parseLeadDate(value: string | null): Date | undefined {
    if (!value) return undefined;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.toDate() : undefined;
}

export function toLeadDateString(date: Date | undefined): string | null {
    if (!date) return null;
    return dayjs(date).format(ISO_DATE);
}

export function todayLeadDateString(now: Date | dayjs.Dayjs = dayjs()): string {
    return dayjs(now).format(ISO_DATE);
}

/** Connect is done once dated; other kinds are done once marked sent. */
export function isTouchSent(touch: Touch): boolean {
    if (touch.kind === "connect") return Boolean(touch.date) || touch.status === "sent";
    if (touch.kind === "email_reply" || touch.kind === "linkedin_reply") return true;
    return touch.status === "sent";
}

export type TouchQueueBucket = "overdue" | "undated" | "today" | "upcoming" | "sent";

const TOUCH_QUEUE_BUCKET_ORDER: Record<TouchQueueBucket, number> = {
    overdue: 0,
    undated: 1,
    today: 2,
    upcoming: 3,
    sent: 4,
};

const TOUCH_KIND_QUEUE_ORDER: Record<Touch["kind"], number> = {
    connect: 0,
    linkedin: 1,
    email: 2,
    linkedin_reply: 3,
    email_reply: 4,
};

export const TOUCH_QUEUE_BUCKET_LABEL: Record<TouchQueueBucket, string> = {
    overdue: "Overdue",
    undated: "No date",
    today: "Today",
    upcoming: "Upcoming",
    sent: "Sent",
};

export function touchQueueBucket(touch: Touch, today = todayLeadDateString()): TouchQueueBucket {
    if (isTouchSent(touch)) return "sent";
    if (!touch.date) return "undated";
    if (touch.date < today) return "overdue";
    if (touch.date === today) return "today";
    return "upcoming";
}

export function compareTouchesForQueue(a: Touch, b: Touch, today = todayLeadDateString()): number {
    const bucketA = touchQueueBucket(a, today);
    const bucketB = touchQueueBucket(b, today);
    if (bucketA !== bucketB) return TOUCH_QUEUE_BUCKET_ORDER[bucketA] - TOUCH_QUEUE_BUCKET_ORDER[bucketB];
    if (a.date && b.date && a.date !== b.date) return a.date.localeCompare(b.date);
    return TOUCH_KIND_QUEUE_ORDER[a.kind] - TOUCH_KIND_QUEUE_ORDER[b.kind];
}

/** Write generated messages onto a lead using the template cadence as written. */
export function applyDraftsToLead(lead: Lead, template: OutreachTemplate, messages: string[]): Lead {
    const timing = template.timing;
    const connectDate =
        lead.touches.find((touch) => touch.kind === "connect" && touch.date)?.date ??
        lead.touches.find((touch) => touch.date)?.date ??
        dayjs();

    if (timing.length === 0) {
        return { ...lead, touches: [] };
    }

    return {
        ...lead,
        ...scheduleFromTiming(connectDate, timing, messages),
    };
}
